/**
 * Company groups (docs/adr/0034-company-groups.md).
 *
 * A group is a set of organisations that are one business — each its own customer, subdomain and
 * subscription, joined so that one person can administer them together. Three things live here:
 *
 *   - creating another company of the group from inside the app (`createCompany`);
 *   - a group owner managing who has access to which companies (`listMembers` … `removeMember`);
 *   - linking an account made on the public sign-up page to an existing login, with proof
 *     (`listLinkCandidates`, `linkAccount`).
 *
 * Every route is kept out of the AI copilot (`ai: 'hidden'`): drafting a password reset or a grant of
 * access across companies is not a proposal a model should make.
 *
 * Company ids in a request only ever CHOOSE AMONG the caller's group's members; the API reads the
 * membership itself (ADR 0034 §7).
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  emailSchema,
  errorBodySchema,
  instantSchema,
  orgIdSchema,
  uuidSchema,
} from '../schemas/common.js';
import {
  orgSlugSchema,
  presentedPasswordSchema,
  recoveryCodeSchema,
  totpCodeSchema,
} from './auth.contract.js';
import { routeMeta } from './meta.js';

const c = initContract();

/**
 * The roles a group owner can give across companies: the built-in ones that apply to the whole
 * company. Not `employee` (self-service belongs to a job), not the department- or team-scoped ones
 * (`hr_manager`, `manager`, `department_head`), and not the implicit desk-agent roles.
 */
export const GROUP_ACCESS_ROLES = [
  'org_owner',
  'hr_admin',
  'payroll_admin',
  'finance',
  'recruiter',
  'auditor',
  'it_admin',
  'expense_manager',
  'performance_admin',
  'project_admin',
  'asset_manager',
  'helpdesk_manager',
  'travel_desk_manager',
] as const;
export const groupAccessRoleSchema = z.enum(GROUP_ACCESS_ROLES);
export type GroupAccessRole = z.infer<typeof groupAccessRoleSchema>;

/** What "Copy settings from this company" can bring over. Never people, pay, documents or PII. */
export const COPYABLE_SETTINGS = [
  'roles',
  'leave_types',
  'holidays',
  'shifts',
  'expense_categories',
  'notifications',
  'branding',
] as const;
export const copyableSettingSchema = z.enum(COPYABLE_SETTINGS);
export type CopyableSetting = z.infer<typeof copyableSettingSchema>;

export const groupCompanySchema = z.object({
  id: orgIdSchema,
  name: z.string(),
  slug: z.string(),
  /** The company this session is signed in to. */
  isCurrent: z.boolean(),
});
export type GroupCompany = z.infer<typeof groupCompanySchema>;

export const myCompanyGroupSchema = z.object({
  /** Null when the caller's company is in no group yet (creating a company starts one). */
  group: z
    .object({
      id: uuidSchema,
      name: z.string(),
      companies: z.array(groupCompanySchema),
    })
    .nullable(),
  /** The caller administers the group: the Group access page is theirs. */
  isOwner: z.boolean(),
  /** "Create another company" is offered: the permission, and — inside a group — ownership of it. */
  canCreateCompany: z.boolean(),
});
export type MyCompanyGroup = z.infer<typeof myCompanyGroupSchema>;

// ---------------------------------------------------------------------------
// Create another company
// ---------------------------------------------------------------------------

export const createCompanyBodySchema = z.object({
  companyName: z.string().trim().min(1).max(200),
  orgSlug: orgSlugSchema,
  /** Settings to copy from the company the caller is signed in to. Empty = start from defaults. */
  copy: z.array(copyableSettingSchema).max(COPYABLE_SETTINGS.length).default([]),
  /** Same clickwrap as sign-up: the new company is a new customer. */
  termsAccepted: z.literal(true),
  termsVersion: z.string().trim().min(1).max(50),
});
export type CreateCompanyRequest = z.infer<typeof createCompanyBodySchema>;

export const createCompanyResponseSchema = z.object({
  organization: z.object({ id: orgIdSchema, name: z.string(), slug: z.string() }),
  /** One-time code that signs the caller in on the new company's own subdomain. */
  handoffCode: z.string().min(16).max(128),
  /** Settings that were asked for but could not be copied; the company exists either way. */
  notCopied: z.array(copyableSettingSchema),
});
export type CreateCompanyResponse = z.infer<typeof createCompanyResponseSchema>;

export const slugAvailabilityResponseSchema = z.object({
  slug: z.string(),
  available: z.boolean(),
});

// ---------------------------------------------------------------------------
// Group access
// ---------------------------------------------------------------------------

export const memberCompanyAccessSchema = z.object({
  orgId: orgIdSchema,
  orgName: z.string(),
  /** Company-wide roles held there, whoever gave them. */
  roles: z.array(z.string()),
  /** The roles Group access added — the only ones removing access takes away. */
  groupAccessRoles: z.array(z.string()),
  /** The person also has a job in this company (they appear in its employee list). */
  isEmployee: z.boolean(),
});

export const groupMemberSchema = z.object({
  /** The person's identity across companies (`CrossOrgIdentityLink.groupId`). */
  identityId: uuidSchema,
  name: z.string(),
  email: z.string(),
  /** Where their password lives — the company they sign in with. */
  homeCompany: z.object({ orgId: orgIdSchema, orgName: z.string() }).nullable(),
  companies: z.array(memberCompanyAccessSchema),
  /** none = never signed in yet; disabled is reserved for the login-access feature. */
  login: z.enum(['active', 'pending', 'none']),
  lastSignInAt: instantSchema.nullable(),
  isOwner: z.boolean(),
  isSelf: z.boolean(),
});
export type GroupMember = z.infer<typeof groupMemberSchema>;

export const listGroupMembersResponseSchema = z.object({
  members: z.array(groupMemberSchema),
});

const companyAccessInputSchema = z.object({
  orgId: orgIdSchema,
  roles: z.array(groupAccessRoleSchema).min(1).max(GROUP_ACCESS_ROLES.length),
});

/** How a person with no login yet gets in: the product's invite-only model, never a password. */
export const signInLinkModeSchema = z.enum(['email', 'copy', 'both']);

export const addGroupMemberBodySchema = z.object({
  email: emailSchema,
  /** Needed only when the email has no account in any company of the group yet. */
  firstName: z.string().trim().min(1).max(100).optional(),
  lastName: z.string().trim().min(1).max(100).optional(),
  companies: z.array(companyAccessInputSchema).min(1).max(50),
  /**
   * Where the login is created when the person has none anywhere in the group. Must be one of
   * `companies`. Ignored when they already sign in somewhere — that stays their home.
   */
  homeOrgId: orgIdSchema.optional(),
  signInLink: signInLinkModeSchema.default('email'),
});
export type AddGroupMemberRequest = z.infer<typeof addGroupMemberBodySchema>;

export const groupMemberWriteResponseSchema = z.object({
  member: groupMemberSchema,
  /** Present only when `signInLink` asked for a link to copy. One-time; never shown again. */
  signInUrl: z.string().url().optional(),
  /** The email a sign-in link was queued to, when one was emailed. */
  emailedTo: z.string().optional(),
});

export const updateGroupMemberBodySchema = z.object({
  /** The full desired state for the companies Group access manages. Companies left out lose it. */
  companies: z.array(companyAccessInputSchema).max(50),
});

export const resetGroupMemberPasswordBodySchema = z.object({
  signInLink: signInLinkModeSchema.default('email'),
});

export const resetGroupMemberPasswordResponseSchema = z.object({
  /** Sessions ended across the group's companies. */
  signedOutSessions: z.number().int(),
  signInUrl: z.string().url().optional(),
  emailedTo: z.string().optional(),
});

export const renameGroupBodySchema = z.object({ name: z.string().trim().min(1).max(120) });

// ---------------------------------------------------------------------------
// Link a company made on the public sign-up page
// ---------------------------------------------------------------------------

export const linkCandidateSchema = z.object({
  orgId: orgIdSchema,
  orgName: z.string(),
  orgSlug: z.string(),
  /** That login uses 2-step verification: `linkAccount` must carry a code. */
  mfaRequired: z.boolean(),
  /** That login owns a group this company can join. */
  groupName: z.string().nullable(),
});

export const listLinkCandidatesResponseSchema = z.object({
  candidates: z.array(linkCandidateSchema),
});

export const linkAccountBodySchema = z.object({
  orgId: orgIdSchema,
  /** The password of the login being linked to — the proof that it is the same person. */
  password: presentedPasswordSchema,
  totpCode: totpCodeSchema.optional(),
  recoveryCode: recoveryCodeSchema.optional(),
  /** Add this company to that login's group, when it owns one and this company is in none. */
  joinGroup: z.boolean().default(true),
});

export const linkAccountResponseSchema = z.object({
  linkedTo: z.object({ orgId: orgIdSchema, orgName: z.string() }),
  joinedGroup: z.string().nullable(),
});

const errors = {
  400: errorBodySchema,
  401: errorBodySchema,
  403: errorBodySchema,
  404: errorBodySchema,
  409: errorBodySchema,
  422: errorBodySchema,
  429: errorBodySchema,
} as const;

const hidden = { ai: 'hidden' } as const;

export const companyGroupContract = c.router(
  {
    getMyGroup: {
      method: 'GET',
      path: '/company-group',
      responses: { 200: myCompanyGroupSchema, ...errors },
      summary:
        "The group the caller's company belongs to, its companies, and whether the caller may " +
        'create companies or manage access',
      metadata: routeMeta(null, hidden),
    },

    renameGroup: {
      method: 'PATCH',
      path: '/company-group',
      body: renameGroupBodySchema,
      responses: { 200: myCompanyGroupSchema, ...errors },
      summary: 'Rename the group (group owners)',
      metadata: routeMeta('org.group:manage', hidden),
    },

    checkCompanySlug: {
      method: 'GET',
      path: '/company-group/companies/slug-availability',
      query: z.object({ slug: orgSlugSchema }),
      responses: { 200: slugAvailabilityResponseSchema, ...errors },
      summary: "Whether a web address is free for a new company (the form's live check)",
      metadata: routeMeta('org.company:create', hidden),
    },

    createCompany: {
      method: 'POST',
      path: '/company-group/companies',
      body: createCompanyBodySchema,
      responses: { 201: createCompanyResponseSchema, ...errors },
      summary:
        'Create another company of the group: its own subdomain, the caller as its Global Admin ' +
        '(no employment), chosen settings copied, linked so it appears in the switch menu',
      metadata: routeMeta('org.company:create', hidden),
    },

    listMembers: {
      method: 'GET',
      path: '/company-group/members',
      responses: { 200: listGroupMembersResponseSchema, ...errors },
      summary:
        'Everyone who has access to companies of the group through Group access, and the owners',
      metadata: routeMeta('org.group:manage', hidden),
    },

    addMember: {
      method: 'POST',
      path: '/company-group/members',
      body: addGroupMemberBodySchema,
      responses: { 201: groupMemberWriteResponseSchema, ...errors },
      summary:
        'Give a person access to chosen companies of the group with chosen roles; a person with no ' +
        'login gets a sign-in link',
      metadata: routeMeta('org.group:manage', hidden),
    },

    updateMember: {
      method: 'PUT',
      path: '/company-group/members/:identityId',
      pathParams: z.object({ identityId: uuidSchema }),
      body: updateGroupMemberBodySchema,
      responses: { 200: groupMemberWriteResponseSchema, ...errors },
      summary:
        'Set which companies and roles Group access gives this person; companies left out lose it ' +
        'and their sessions there end',
      metadata: routeMeta('org.group:manage', hidden),
    },

    resetMemberPassword: {
      method: 'POST',
      path: '/company-group/members/:identityId/reset-password',
      pathParams: z.object({ identityId: uuidSchema }),
      body: resetGroupMemberPasswordBodySchema,
      responses: { 200: resetGroupMemberPasswordResponseSchema, ...errors },
      summary:
        "Scramble the person's password, sign them out of every company of the group, and issue a " +
        'new sign-in link',
      metadata: routeMeta('org.group:manage', hidden),
    },

    removeMember: {
      method: 'DELETE',
      path: '/company-group/members/:identityId',
      pathParams: z.object({ identityId: uuidSchema }),
      body: c.noBody(),
      responses: { 200: z.object({ removedFrom: z.number().int() }), ...errors },
      summary:
        'Remove everything Group access gave this person, in every company; their own jobs and ' +
        'logins elsewhere are untouched',
      metadata: routeMeta('org.group:manage', hidden),
    },

    listLinkCandidates: {
      method: 'GET',
      path: '/company-group/link-candidates',
      responses: { 200: listLinkCandidatesResponseSchema, ...errors },
      summary:
        "Other companies where the caller's own verified email already signs in, which this " +
        'account could be linked to. Empty until the mailbox is verified.',
      metadata: routeMeta(null, hidden),
    },

    linkAccount: {
      method: 'POST',
      path: '/company-group/link-account',
      body: linkAccountBodySchema,
      responses: { 200: linkAccountResponseSchema, ...errors },
      summary:
        "Link this account to the caller's login in another company, proven by that login's " +
        'password (and 2-step code); this account then signs in with that login',
      metadata: routeMeta(null, hidden),
    },
  },
  { pathPrefix: '' },
);

export type CompanyGroupContract = typeof companyGroupContract;
